// Роли и скоупы обучающих модулей — словарь способностей формата.
//
// Всё, что здесь объявлено, поглощает четыре (по факту — шесть, см. §2.8 п.2)
// прото-реестра форматов: `COVERAGE_KINDS`, `TOPIC_KIT_KINDS`, `RECOMMEND_ORDER`
// + `DEFAULT_MINUTES`, DB-enum `study_activity_type`, `subsectionMaterialKind`,
// `ACTIVITY_TYPES`. Свести их — задача Ш2; здесь заводится словарь, к которому
// они сводятся.

import { z } from "zod";
import { COVERAGE_SCOPES } from "../contracts/learning/schemas.js";

/**
 * Шаг спины темы (① Разобраться → ② Проверить → ③ Добрать).
 *
 * Роль — свойство формата, а не расположение в UI: панель сама решает, в какой
 * колонке рисовать `retrieve`-модули. Спина фиксирована и в манифесты не
 * выносится (три шага — продуктовый инвариант Course Workspace V2).
 */
export const MODULE_LEARNING_ROLES = ["encode", "retrieve", "elaborate"] as const;
export const moduleLearningRoleSchema = z.enum(MODULE_LEARNING_ROLES);
export type ModuleLearningRole = (typeof MODULE_LEARNING_ROLES)[number];

/**
 * Где модуль умеет работать.
 *
 * Выводится из `COVERAGE_SCOPES` (`course` | `topic` | `material`) + `standalone`
 * — четвёртое значение для инструментов со своим циклом (эссе, визуальный
 * разбор): у них нет покрытия темы, но есть собственный вход.
 *
 * ⚠️ Соответствие с `GenerationSource` (contracts/knowledge-base) НЕ один-к-одному,
 * вопреки комментарию ТЗ §4.2:
 *   `GenerationSource.material`   ↔ scope `material`
 *   `GenerationSource.subsection` ↔ scope `topic`
 *   `GenerationSource.set`        ↔ scope `course` (когда набор — `course_source`),
 *                                    либо обычная папка Библиотеки (скоупа нет)
 * Генерация «по папке» — не учебный скоуп, а вход движка генерации. Ш2 обязан
 * либо сузить `GenerationSource`, либо явно задокументировать разрыв в маппере;
 * молча приравнивать `set` к `course` нельзя — курс из шаблона имеет набор
 * источников, недоступных пользователю (декрет приватности course-templates).
 */
export const MODULE_SCOPES = [...COVERAGE_SCOPES, "standalone"] as const;
export const moduleScopeSchema = z.enum(MODULE_SCOPES);
export type ModuleScope = (typeof MODULE_SCOPES)[number];

/**
 * Участие формата в комплекте темы.
 *
 * - `auto`       — входит в авто-комплект JIT (сегодня это пять текстовых видов);
 * - `on-demand`  — кнопка в панели, генерация по клику;
 * - `preference` — `on-demand` + предгенерация при выявленном предпочтении
 *                  (`study_plans.audio_streak` ≥ `AUDIO_PREFERENCE_THRESHOLD`);
 * - `none`       — канал панели без собственного артефакта (чат, внешний модуль).
 *
 * Порог предпочтения остаётся в `learning/config.ts`: манифест отвечает на
 * «кто участвует», конфиг — на «сколько/когда» (ТЗ §4.3).
 */
export const TOPIC_GENERATION_MODES = ["auto", "on-demand", "preference", "none"] as const;
export const topicGenerationModeSchema = z.enum(TOPIC_GENERATION_MODES);
export type TopicGenerationMode = (typeof TOPIC_GENERATION_MODES)[number];
